import { useMutation, useQuery } from '@apollo/client';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { Button } from '@ovComponents/2-component/button/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import { useCallback, useContext, useMemo, useState } from 'react';
import { FETCH_SUB_TRADE_REQUESTS_FULL } from '../queries';
import {
  ALIGNED_TABLE_SX,
  BOLD_SX,
  LABEL_SX,
  LABEL_UPPER_SX,
  SECONDARY_TEXT_SX,
  SMALL_SX,
  TABULAR_SX,
  TRUNCATE_SX,
} from '../styles';
import { useTradingHub } from '../tradingHubContext';
import { formatMoney, getPortfolioName, tradeSideColor } from '../utils';
import {
  BUCKET_SUB_TRADE_REQUESTS,
  CREATE_SUB_TRADE_REQUESTS,
} from '../utils/mutations';

type StagedTrade = {
  id: string;
  portfolioReportId: string;
  portfolioName: string;
  accountType: string;
  subAccountId: string;
  financialProductId: string;
  ticker: string;
  productName: string;
  currency: string;
  side: 'BUY' | 'SELL';
  amountCents: number;
  currentValueCents: number;
  expectedValueCents: number;
  currentPct: number;
  targetPct: number;
  removed: boolean;
  modelPortfolioType: string;
  objectId: string;
};

type TickerGroup = {
  ticker: string;
  productName: string;
  currency: string;
  totalBuyCents: number;
  totalSellCents: number;
  buyCount: number;
  sellCount: number;
  trades: StagedTrade[];
};

type BulkTradeStagingProps = {
  selectedPortfolios: any[];
  organizationId: string;
  onClose: () => void;
  onTradesCreated: () => void;
};

const getAccountType = (report: any): string =>
  report.modelPortfolioType === 'GOAL'
    ? 'Goal'
    : report.object?.account?.type || 'Account';
const getSubAccountId = (report: any): string =>
  report.subAccounts?.[0]?.subAccount?.id || report.object?.id || '';

const BulkTradeStaging = ({
  selectedPortfolios,
  organizationId,
  onClose,
  onTradesCreated,
}: BulkTradeStagingProps) => {
  const { sys } = useThemeTokens();
  const { activeOrganization } = useContext(UserContext);
  const [createSubTradeRequests, { loading: creating }] = useMutation(
    CREATE_SUB_TRADE_REQUESTS,
  );
  const [bucketSubTradeRequests] = useMutation(BUCKET_SUB_TRADE_REQUESTS);
  const { setLastBulkCreationTime } = useTradingHub();

  const { data: pendingSTRData } = useQuery(FETCH_SUB_TRADE_REQUESTS_FULL, {
    variables: {
      input: {
        filter: { organizationId, states: ['INITIATED', 'READY'] },
        pagination: {
          perPage: 500,
          offSet: 0,
          sortField: 'createdAt',
          sortDesc: true,
        },
      },
    },
    skip: !organizationId,
    fetchPolicy: 'cache-and-network',
  });

  const selectedSubAccountIds = useMemo(
    () => new Set(selectedPortfolios.map(getSubAccountId)),
    [selectedPortfolios],
  );

  const conflictKeys = useMemo(() => {
    const keys = new Set<string>();
    const existingSTRs =
      pendingSTRData?.fetchSubTradeRequests?.subTradeRequests || [];
    for (const str of existingSTRs) {
      const saId = str.subAccount?.id;
      const fpId = str.financialProduct?.id;
      if (saId && fpId && selectedSubAccountIds.has(saId)) {
        keys.add(`${saId}:${fpId}`);
      }
    }
    return keys;
  }, [pendingSTRData, selectedSubAccountIds]);

  const initialTrades = useMemo(() => {
    const trades: StagedTrade[] = [];
    for (const report of selectedPortfolios) {
      const mv =
        report.object?.statistics?.marketValueCents ||
        report.marketValueCents ||
        1;
      const holdings = report.holdings || [];
      const subAccountId = getSubAccountId(report);
      const portfolioName = getPortfolioName(report);
      const accountType = getAccountType(report);
      const modelPortfolioType = report.modelPortfolioType || 'SUB_ACCOUNT';
      const objectId = report.object?.id || '';
      const rawDeltas: Array<{
        idx: number;
        h: (typeof holdings)[0];
        delta: number;
        side: 'BUY' | 'SELL';
        absDelta: number;
        curPct: number;
        tgtPct: number;
      }> = [];
      let totalSellCents = 0,
        totalBuyCents = 0;

      for (let i = 0; i < holdings.length; i++) {
        const h = holdings[i];
        const isCash = !!h.financialProduct?.isCash || !!h.isCashEquivalent;
        if (isCash) continue;
        const targetValueCents = Math.round((h.expectedPercentage || 0) * mv);
        const delta = targetValueCents - (h.valueCents || 0);
        if (delta === 0) continue;
        const side: 'BUY' | 'SELL' = delta > 0 ? 'BUY' : 'SELL';
        const absDelta = Math.abs(delta);
        const curPct = ((h.valueCents || 0) / mv) * 100;
        const tgtPct = (h.expectedPercentage || 0) * 100;
        if (side === 'SELL') totalSellCents += absDelta;
        else totalBuyCents += absDelta;
        rawDeltas.push({ idx: i, h, delta, side, absDelta, curPct, tgtPct });
      }

      const cashAvailable = report.cashAvailableForTradeCents || 0;
      const buyingPower = cashAvailable + totalSellCents;
      const buyScale =
        totalBuyCents > 0 && totalBuyCents > buyingPower
          ? buyingPower / totalBuyCents
          : 1;

      for (const entry of rawDeltas) {
        const cappedAmount =
          entry.side === 'BUY'
            ? Math.round(entry.absDelta * buyScale)
            : entry.absDelta;
        if (cappedAmount === 0) continue;
        trades.push({
          id: `${report.id}-${entry.idx}`,
          portfolioReportId: report.id,
          portfolioName,
          accountType,
          subAccountId,
          financialProductId: entry.h.financialProduct?.id || '',
          ticker: entry.h.financialProduct?.ticker || 'UNKNOWN',
          productName:
            entry.h.financialProduct?.translatedName?.en ||
            entry.h.financialProduct?.name ||
            entry.h.financialProduct?.ticker ||
            '',
          currency:
            entry.h.currency || entry.h.financialProduct?.currency || 'CAD',
          side: entry.side,
          amountCents: cappedAmount,
          currentValueCents: entry.h.valueCents || 0,
          expectedValueCents: entry.h.expectedValueCents || 0,
          currentPct: entry.curPct,
          targetPct: entry.tgtPct,
          removed: false,
          modelPortfolioType,
          objectId,
        });
      }
    }
    return trades;
  }, [selectedPortfolios]);

  const [trades, setTrades] = useState<StagedTrade[]>(initialTrades);

  const toggleRemoveTrade = useCallback((tradeId: string) => {
    setTrades((prev) =>
      prev.map((t) => (t.id === tradeId ? { ...t, removed: !t.removed } : t)),
    );
  }, []);

  const removePortfolio = useCallback((portfolioReportId: string) => {
    setTrades((prev) =>
      prev.map((t) =>
        t.portfolioReportId === portfolioReportId ? { ...t, removed: true } : t,
      ),
    );
  }, []);

  const activeTrades = useMemo(
    () =>
      trades.filter(
        (t) =>
          !t.removed &&
          !conflictKeys.has(`${t.subAccountId}:${t.financialProductId}`),
      ),
    [trades, conflictKeys],
  );

  const conflictCount = useMemo(
    () =>
      trades.filter(
        (t) =>
          !t.removed &&
          conflictKeys.has(`${t.subAccountId}:${t.financialProductId}`),
      ).length,
    [trades, conflictKeys],
  );

  const tickerGroups = useMemo(() => {
    const map = new Map<string, TickerGroup>();
    for (const t of activeTrades) {
      const existing = map.get(t.ticker);
      if (existing) {
        if (t.side === 'BUY') {
          existing.totalBuyCents += t.amountCents;
          existing.buyCount++;
        } else {
          existing.totalSellCents += t.amountCents;
          existing.sellCount++;
        }
        existing.trades.push(t);
      } else {
        map.set(t.ticker, {
          ticker: t.ticker,
          productName: t.productName,
          currency: t.currency,
          totalBuyCents: t.side === 'BUY' ? t.amountCents : 0,
          totalSellCents: t.side === 'SELL' ? t.amountCents : 0,
          buyCount: t.side === 'BUY' ? 1 : 0,
          sellCount: t.side === 'SELL' ? 1 : 0,
          trades: [t],
        });
      }
    }
    return Array.from(map.values()).sort(
      (a, b) =>
        b.totalBuyCents +
        b.totalSellCents -
        (a.totalBuyCents + a.totalSellCents),
    );
  }, [activeTrades]);

  const stats = useMemo(() => {
    const portfolioIds = new Set(activeTrades.map((t) => t.portfolioReportId));
    const buys = activeTrades.filter((t) => t.side === 'BUY');
    const sells = activeTrades.filter((t) => t.side === 'SELL');
    const totalBuyCents = buys.reduce((s, t) => s + t.amountCents, 0);
    const totalSellCents = sells.reduce((s, t) => s + t.amountCents, 0);
    return {
      portfolioCount: portfolioIds.size,
      tradeCount: activeTrades.length,
      buyCount: buys.length,
      sellCount: sells.length,
      totalBuyCents,
      totalSellCents,
      netCashImpactCents: totalSellCents - totalBuyCents,
    };
  }, [activeTrades]);

  const [expandedTickers, setExpandedTickers] = useState<Set<string>>(
    new Set(),
  );
  const toggleTickerExpand = useCallback((ticker: string) => {
    setExpandedTickers((prev) => {
      const next = new Set(prev);
      if (next.has(ticker)) next.delete(ticker);
      else next.add(ticker);
      return next;
    });
  }, []);

  const handleCreateTrades = async () => {
    if (activeTrades.length === 0) return;
    try {
      // Group by source — backend requires source+sourceId per call (legacy BulkTradeRequest mechanism)
      // GOAL portfolios use objectId (goal ID), SUB_ACCOUNT portfolios use subAccountId
      const tradesBySource = new Map<
        string,
        {
          source: 'GOAL' | 'SUB_ACCOUNT';
          sourceId: string;
          trades: typeof activeTrades;
        }
      >();
      for (const t of activeTrades) {
        if (!t.financialProductId) continue;
        const isGoal = t.modelPortfolioType === 'GOAL';
        const source = isGoal ? 'GOAL' : 'SUB_ACCOUNT';
        const sourceId = isGoal ? t.objectId || t.subAccountId : t.subAccountId;
        const key = `${source}:${sourceId}`;
        const group = tradesBySource.get(key) ?? {
          source,
          sourceId,
          trades: [],
        };
        group.trades.push(t);
        tradesBySource.set(key, group);
      }

      for (const { source, sourceId, trades } of Array.from(
        tradesBySource.values(),
      )) {
        const subTradeRequests = trades.map((t) => ({
          subAccountId: t.subAccountId,
          financialProductId: t.financialProductId,
          portfolioReportId: t.portfolioReportId,
          type: t.side,
          moneyAllocatedCents: t.amountCents,
          currency: t.currency,
        }));
        await createSubTradeRequests({
          variables: { input: { source, sourceId, subTradeRequests } },
        });
      }

      if (activeOrganization?.bulkTradingProcess === 'BUCKET') {
        for (const { source, sourceId } of Array.from(
          tradesBySource.values(),
        )) {
          try {
            await bucketSubTradeRequests({
              variables: {
                input: {
                  source,
                  sourceId,
                  ...(organizationId && { organizationId }),
                },
              },
            });
          } catch {
            // Non-fatal — trade was created; bucket failure shouldn't block completion
          }
        }
      }

      setLastBulkCreationTime(Date.now());
      onTradesCreated();
    } catch (err) {
      console.error('Failed to create bulk trades:', err);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      sx={{
        // ovComponents Dialog does not expose paperProps — MUI class selector required for sizing
        '.MuiDialog-paper': {
          width: { xs: '98%', md: '92%', lg: '88%' },
          maxWidth: 1200,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle onClose={onClose}>
        <Box display='flex' alignItems='baseline' gap={0.75}>
          <Typography variant='bodyMedium' sx={{ fontWeight: 800 }}>
            Bulk Trade Preview
          </Typography>
          <Typography variant='labelSmall' sx={{ ...SECONDARY_TEXT_SX }}>
            Autofill to Target — {stats.portfolioCount} portfolio
            {stats.portfolioCount !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: '0 !important' }}>
        <Box
          sx={{
            px: 2,
            py: 0.75,
            borderBottom: '1px solid',
            borderColor: sys.color.outlineVariant,
            bgcolor: sys.color.surface,
            display: 'flex',
            gap: 3,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <StatPill
            label='Portfolios'
            value={String(stats.portfolioCount)}
            sys={sys as any}
          />
          <StatPill
            label='Total Trades'
            value={`${stats.tradeCount} (${stats.buyCount}B / ${stats.sellCount}S)`}
            sys={sys as any}
          />
          <StatPill
            label='Buy Value'
            value={formatMoney(stats.totalBuyCents)}
            color={sys.color.positive}
            sys={sys as any}
          />
          <StatPill
            label='Sell Value'
            value={formatMoney(stats.totalSellCents)}
            color={sys.color.negative}
            sys={sys as any}
          />
          <StatPill
            label={
              stats.netCashImpactCents >= 0
                ? 'Net Cash Freed'
                : 'Net Cash Required'
            }
            value={formatMoney(Math.abs(stats.netCashImpactCents))}
            color={
              stats.netCashImpactCents >= 0
                ? sys.color.positive
                : sys.color.warning
            }
            sys={sys as any}
          />
        </Box>

        {conflictCount > 0 && (
          <Box
            sx={{
              px: 2,
              py: 0.75,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: sys.color.warningVariant,
              borderBottom: '1px solid',
              borderColor: sys.color.outlineVariant,
            }}
          >
            <Typography
              variant='labelSmall'
              sx={{ ...LABEL_SX, fontWeight: 700, color: sys.color.warning }}
            >
              {conflictCount} trade{conflictCount !== 1 ? 's' : ''} skipped
            </Typography>
            <Typography
              variant='labelSmall'
              sx={{ fontSize: '0.65rem', color: sys.color.onSurfaceVariant }}
            >
              — a pending trade already exists for this holding. Review in the
              Trades tab.
            </Typography>
          </Box>
        )}

        <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1 }}>
          {tickerGroups.length === 0 ? (
            <Box
              display='flex'
              justifyContent='center'
              alignItems='center'
              py={6}
            >
              <Typography
                variant='bodySmall'
                sx={{ color: sys.color.onDisabled }}
              >
                No trades to stage. All holdings are within target allocation.
              </Typography>
            </Box>
          ) : (
            <Table sx={{ ...ALIGNED_TABLE_SX }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& td, & th': {
                      ...LABEL_UPPER_SX,
                      padding: '6px 12px',
                      fontWeight: 700,
                      color: sys.color.onSurfaceVariant,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  <TableCell sx={{ width: '22%' }}>Ticker</TableCell>
                  <TableCell sx={{ width: '28%' }}>Product</TableCell>
                  <TableCell sx={{ textAlign: 'right', width: '15%' }}>
                    Buys
                  </TableCell>
                  <TableCell sx={{ textAlign: 'right', width: '15%' }}>
                    Sells
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center', width: '8%' }}>
                    Trades
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center', width: '8%' }}>
                    Ccy
                  </TableCell>
                  <TableCell sx={{ width: 32 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {tickerGroups.map((group) => (
                  <TickerGroupRow
                    key={group.ticker}
                    group={group}
                    expanded={expandedTickers.has(group.ticker)}
                    onToggleExpand={() => toggleTickerExpand(group.ticker)}
                    onRemoveTrade={toggleRemoveTrade}
                    onRemovePortfolio={removePortfolio}
                    sys={sys as any}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </DialogContent>

      <DialogFooter>
        <Typography
          variant='labelSmall'
          sx={{ ...SECONDARY_TEXT_SX, mr: 'auto' }}
        >
          {activeTrades.length} trade{activeTrades.length !== 1 ? 's' : ''}{' '}
          across {stats.portfolioCount} portfolio
          {stats.portfolioCount !== 1 ? 's' : ''} will be created in Staged
          state
        </Typography>
        <Button label='Cancel' variant='text' size='sm' onClick={onClose} />
        <Button
          label={
            creating
              ? 'Creating...'
              : `Create ${activeTrades.length} Trade${activeTrades.length !== 1 ? 's' : ''}`
          }
          variant='filled'
          size='sm'
          disabled={activeTrades.length === 0 || creating}
          onClick={handleCreateTrades}
        />
      </DialogFooter>
    </Dialog>
  );
};

const StatPill = ({
  label,
  value,
  color,
  sys,
}: {
  label: string;
  value: string;
  color?: string;
  sys: { color: Record<string, string> };
}) => (
  <Box>
    <Typography variant='labelSmall' sx={{ color: sys.color.onDisabled }}>
      {label}
    </Typography>
    <Typography
      variant='bodySmall'
      sx={{ ...BOLD_SX, color: color ?? sys.color.onSurface }}
    >
      {value}
    </Typography>
  </Box>
);

const CELL_PX = '4px 12px';

const SideValueCell = ({
  isBuy,
  value,
  show,
  sys,
}: {
  isBuy: boolean;
  value: string;
  show: boolean;
  sys: { color: Record<string, string> };
}) =>
  show ? (
    <Typography
      variant='bodySmall'
      sx={{ ...TABULAR_SX, color: tradeSideColor(sys.color, isBuy) }}
    >
      {value}
    </Typography>
  ) : (
    <Typography variant='labelSmall' sx={{ color: 'text.disabled' }}>
      —
    </Typography>
  );

const TickerGroupRow = ({
  group,
  expanded,
  onToggleExpand,
  onRemoveTrade,
  sys,
}: {
  group: TickerGroup;
  expanded: boolean;
  onToggleExpand: () => void;
  onRemoveTrade: (id: string) => void;
  onRemovePortfolio: (portfolioReportId: string) => void;
  sys: { color: Record<string, string> };
}) => (
  <>
    <TableRow
      onClick={onToggleExpand}
      sx={{
        cursor: 'pointer',
        '&:hover': { bgcolor: sys.color.neutralHover },
        bgcolor: expanded ? sys.color.primaryVariant : undefined,
      }}
    >
      <TableCell sx={{ padding: CELL_PX }}>
        <Box display='flex' alignItems='center' gap={0.5}>
          <ExpandMoreIcon
            sx={{
              fontSize: 16,
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.15s',
              color: sys.color.onSurfaceVariant,
            }}
          />
          <Typography variant='bodySmall' sx={{ fontWeight: 700 }}>
            {group.ticker}
          </Typography>
        </Box>
      </TableCell>
      <TableCell sx={{ padding: CELL_PX }}>
        <Typography
          variant='labelSmall'
          sx={{ ...SECONDARY_TEXT_SX, ...TRUNCATE_SX }}
        >
          {group.productName}
        </Typography>
      </TableCell>
      <TableCell number sx={{ padding: CELL_PX }}>
        <SideValueCell
          isBuy
          value={`+${formatMoney(group.totalBuyCents)}`}
          show={group.totalBuyCents > 0}
          sys={sys as any}
        />
      </TableCell>
      <TableCell number sx={{ padding: CELL_PX }}>
        <SideValueCell
          isBuy={false}
          value={formatMoney(group.totalSellCents)}
          show={group.totalSellCents > 0}
          sys={sys as any}
        />
      </TableCell>
      <TableCell center sx={{ padding: CELL_PX }}>
        <Typography variant='labelSmall'>
          {group.buyCount + group.sellCount}
        </Typography>
      </TableCell>
      <TableCell center sx={{ padding: CELL_PX }}>
        <Typography variant='labelSmall' sx={{ ...SECONDARY_TEXT_SX }}>
          {group.currency}
        </Typography>
      </TableCell>
      <TableCell sx={{ padding: CELL_PX }} />
    </TableRow>

    {expanded &&
      group.trades.map((trade) => (
        <TableRow
          key={trade.id}
          sx={{
            bgcolor: sys.color.primaryVariant,
            '&:hover': { bgcolor: sys.color.neutralHover },
          }}
        >
          <TableCell sx={{ padding: CELL_PX, pl: 4 }}>
            <Typography
              variant='labelSmall'
              sx={{ fontWeight: 600, ...TRUNCATE_SX }}
            >
              {trade.portfolioName}
            </Typography>
          </TableCell>
          <TableCell sx={{ padding: CELL_PX }}>
            <Box display='flex' alignItems='center' gap={0.5}>
              <Badge label={trade.accountType} color='neutral' />
              <Typography
                variant='labelSmall'
                sx={{ ...SECONDARY_TEXT_SX, ...SMALL_SX }}
              >
                {trade.currentPct.toFixed(1)}% → {trade.targetPct.toFixed(1)}%
              </Typography>
            </Box>
          </TableCell>
          <TableCell number sx={{ padding: CELL_PX }}>
            <SideValueCell
              isBuy
              value={formatMoney(trade.amountCents)}
              show={trade.side === 'BUY'}
              sys={sys as any}
            />
          </TableCell>
          <TableCell number sx={{ padding: CELL_PX }}>
            <SideValueCell
              isBuy={false}
              value={formatMoney(trade.amountCents)}
              show={trade.side === 'SELL'}
              sys={sys as any}
            />
          </TableCell>
          <TableCell center sx={{ padding: CELL_PX }}>
            <Badge
              label={trade.side}
              color={trade.side === 'BUY' ? 'positive' : 'negative'}
            />
          </TableCell>
          <TableCell center sx={{ padding: CELL_PX }}>
            <Typography variant='labelSmall' sx={{ ...SECONDARY_TEXT_SX }}>
              {trade.currency}
            </Typography>
          </TableCell>
          <TableCell sx={{ padding: CELL_PX }}>
            <IconButton
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onRemoveTrade(trade.id);
              }}
              sx={{ p: 0.25 }}
            >
              <DeleteOutlineIcon
                sx={{ fontSize: 14, color: sys.color.onDisabled }}
              />
            </IconButton>
          </TableCell>
        </TableRow>
      ))}
  </>
);

export default BulkTradeStaging;
